import { CSVParserService } from "./csv-parser.service";
import { addressRepository } from "@/db/repositories/address.repository";
import { logRepository } from "@/db/repositories/log.repository";
import { db } from "@/db/db";
import { CONSTANTS } from "@/config/constants";
import { chunkArray } from "@/utils/string.utils";
import { formatCall } from "@/utils/string.utils";
import { Address } from "@/types/address.types";

export interface ImportResult {
  successCount: number;
  failureCount: number;
}

export class CSVImportService {
  private parser = new CSVParserService();

  async importCSV(
    file: File,
    onProgress?: (progress: number, total: number) => void
  ): Promise<ImportResult> {
    const records = await this.parser.parseCSVFile(file);
    const batches = chunkArray(records, CONSTANTS.BATCH_SIZE);

    let successCount = 0;
    let failureCount = 0;

    // 获取所有现有地址，构建呼号到地址的映射表
    const existingAddresses = await addressRepository.findAll();
    const existingAddressMap = new Map<string, Address>();
    existingAddresses.forEach(addr => {
      existingAddressMap.set(addr.call, addr);
    });

    for (let i = 0; i < batches.length; i++) {
      const batch = batches[i];
      if (!batch) continue;

      const processedAddresses: Address[] = [];
      
      for (const record of batch) {
        const formattedCall = formatCall(record.call);
        const existingAddress = existingAddressMap.get(formattedCall);
        const now = new Date().toISOString();
        
        if (existingAddress) {
          // 更新现有地址
          processedAddresses.push({
            ...existingAddress,
            address: record.address,
            zip: record.zip,
            status: record.note ? "invalid" : "valid",
            outsea: record.outsea === "Y",
            updatedAt: now,
          });
        } else {
          // 创建新地址
          processedAddresses.push({
            id: this.generateId(),
            call: formattedCall,
            address: record.address,
            zip: record.zip,
            status: record.note ? "invalid" : "valid",
            outsea: record.outsea === "Y",
            createdAt: now,
            updatedAt: now,
          });
        }
      }

      try {
        // 使用 bulkPut 执行批量插入和更新
        await db.addresses.bulkPut(processedAddresses);
        successCount += batch.length;
        
        // 更新映射表，确保后续批次能使用最新数据
        processedAddresses.forEach(addr => {
          existingAddressMap.set(addr.call, addr);
        });
      } catch {
        failureCount += batch.length;
      }

      if (onProgress) {
        onProgress((i + 1) * CONSTANTS.BATCH_SIZE, records.length);
      }
    }

    await logRepository.create({
      operation: "import",
      resource: "address",
      status: "success",
      details: `成功导入 ${successCount} 条地址，失败 ${failureCount} 条`,
    });

    return { successCount, failureCount };
  }

  private generateId(): string {
    return `address_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
}
